import {
  PRIORITY_COLOR,
  ISSUE_TYPE_COLOR,
  STATE_COLOR,
  TICKET_STATE_COLOR,
  formatStateLabel,
  resolveAssignees
} from '../../functions/tickets/ticketUtils.js'

const MAX_VISIBLE_LABELS = 3
const MAX_VISIBLE_ASSIGNEES = 3

// Strip markdown noise from issue body so the card preview reads like
// plain prose. Light-touch — full markdown rendering belongs in the detail
// modal, not on the board.
const _previewText = (body) => {
  if (!body) return ''
  return body
    .replace(/```[\s\S]*?```/g, '')          // fenced code
    .replace(/`([^`]+)`/g, '$1')             // inline code
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')    // images
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1') // links → text
    .replace(/^#{1,6}\s+/gm, '')             // headings
    .replace(/[*_~]{1,3}([^*_~]+)[*_~]{1,3}/g, '$1') // emphasis
    .replace(/^\s*[-*+]\s+/gm, '')           // bullets
    .replace(/<[^>]+>/g, '')                 // html tags
    .replace(/\r?\n+/g, ' ')                 // collapse newlines
    .replace(/\s+/g, ' ')
    .trim()
}

export const TicketsCard = {
  flow: 'y',
  gap: 'Y',
  padding: 'Z A',
  // Browser-native viewport culling — `auto` skips rendering off-screen
  // cards, `contain-intrinsic-size` reserves a slot so layout + scrollbar
  // remain stable. We give a generous range so the dynamic content
  // (variable label/description sizes) never gets clipped before the
  // browser actually paints.
  contentVisibility: 'auto',
  containIntrinsicSize: 'auto 180px',
  round: 'Z1',
  background: 'overlay',
  borderWidth: 'V',
  borderColor: 'line',
  borderStyle: 'solid',
  cursor: 'pointer',
  position: 'relative',
  boxShadow: '0 1px 2px var(--color-shadow)',
  ':hover': {
    borderColor: 'overlayStrong',
    transform: 'translateY(-1px)',
    boxShadow: '0 3px 12px var(--color-shadow)'
  },
  ':active': { transform: 'translateY(0)' },
  transition: 'transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease',
  draggable: 'true',
  'data-ticket-id': (el, s) => s.id,
  onDragstart: (e, el, s) => {
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/ticket-id', String(s.id))
    el.state.root.update({ activeDrag: { id: s.id } })
  },
  onDragend: (e, el, s) => {
    el.state.root.update({ activeDrag: null })
  },
  onClick: (e, el, s) => {
    el.state.root.update({ selectedTicket: { ...s } })
  },

  // Top tag row — issue type, epic, state. Compact pills with subtle
  // tinted backgrounds (10% opacity of the tag colour) instead of raw
  // borders, giving a softer, more modern look.
  TagRow: {
    display: (el, s) => (s.issueType || s.epicName || s.state) ? 'flex' : 'none',
    flow: 'x',
    align: 'center left',
    gap: 'Y',
    flexWrap: 'wrap',

    IssueType: {
      display: (el, s) => s.issueType ? 'inline-flex' : 'none',
      tag: 'span',
      align: 'center center',
      padding: 'V Z',
      round: 'C',
      fontSize: 'V',
      fontWeight: '700',
      letterSpacing: '.06em',
      color: (el, s) => ISSUE_TYPE_COLOR[s.issueType] || 'caption',
      background: (el, s) => `color-mix(in srgb, var(--color-${ISSUE_TYPE_COLOR[s.issueType] || 'caption'}) 14%, transparent)`,
      text: (el, s) => (s.issueType || '').toUpperCase()
    },

    Epic: {
      display: (el, s) => s.epicName ? 'inline-flex' : 'none',
      tag: 'span',
      align: 'center center',
      gap: 'V',
      padding: 'V Z',
      round: 'C',
      fontSize: 'V',
      fontWeight: '600',
      letterSpacing: '.04em',
      color: 'magenta',
      background: 'color-mix(in srgb, var(--color-magenta) 14%, transparent)',
      EpicLabel: { tag: 'span', fontWeight: '700', text: 'EPIC' },
      Sep: { tag: 'span', opacity: '.5', text: '·' },
      Name: { tag: 'span', text: (el, s) => (el.parent.parent.state.epicName || '').toUpperCase() }
    },

    State: {
      display: (el, s) => s.state ? 'inline-flex' : 'none',
      tag: 'span',
      align: 'center center',
      gap: 'V',
      padding: 'V Z',
      round: 'C',
      fontSize: 'V',
      fontWeight: '700',
      letterSpacing: '.06em',
      color: (el, s) => TICKET_STATE_COLOR[s.state] || STATE_COLOR[s.state] || 'caption',
      background: (el, s) => `color-mix(in srgb, var(--color-${TICKET_STATE_COLOR[s.state] || STATE_COLOR[s.state] || 'caption'}) 14%, transparent)`,
      Dot: {
        width: 'V',
        height: 'V',
        round: '50%',
        background: 'currentColor'
      },
      Text: { tag: 'span', text: (el, s) => formatStateLabel(el.parent.parent.state.state) }
    },

    PriorityDot: {
      display: (el, s) => s.priority ? 'inline-flex' : 'none',
      title: (el, s) => `Priority: ${s.priority || ''}`,
      width: 'Z',
      height: 'Z',
      round: '50%',
      marginLeft: 'auto',
      alignSelf: 'center',
      background: (el, s) => PRIORITY_COLOR[s.priority] || 'caption',
      boxShadow: (el, s) => `0 0 8px color-mix(in srgb, var(--color-${PRIORITY_COLOR[s.priority] || 'caption'}) 40%, transparent)`
    }
  },

  // Title — up to 2 lines, ellipsised. Editorial feel with tight
  // letter-spacing and a comfortable line-height. Uses raw `style:` for
  // line-clamp because DOMQL doesn't translate webkit-prefixed props.
  Title: {
    tag: 'h3',
    fontSize: 'Z2',
    fontWeight: '500',
    letterSpacing: '-.01em',
    color: 'title',
    margin: '0',
    lineHeight: '1.35',
    style: {
      display: '-webkit-box',
      overflow: 'hidden',
      '-webkit-line-clamp': '2',
      '-webkit-box-orient': 'vertical'
    },
    text: (el, s) => s.title || '(no title)'
  },

  // Description preview — markdown stripped, 2 lines max. Hidden when
  // body is empty so the card collapses cleanly.
  Description: {
    display: (el, s) => _previewText(s.body) ? '-webkit-box' : 'none',
    tag: 'p',
    margin: '0',
    fontSize: 'Y2',
    lineHeight: '1.5',
    color: 'caption',
    style: {
      overflow: 'hidden',
      '-webkit-line-clamp': '2',
      '-webkit-box-orient': 'vertical'
    },
    text: (el, s) => _previewText(s.body)
  },

  // Label chips with overflow indicator. The +N chip clicks through to the
  // detail modal like the rest of the card.
  Labels: {
    display: (el, s) => (s.labels || []).length ? 'flex' : 'none',
    flow: 'x',
    flexWrap: 'wrap',
    gap: 'Y',

    childExtends: 'TicketsLabelChip',
    childrenAs: 'state',
    children: (el, s) => {
      const labels = el.parent.state.labels || []
      const visible = labels.slice(0, MAX_VISIBLE_LABELS).map(label => ({ label, more: 0 }))
      const remaining = labels.length - MAX_VISIBLE_LABELS
      if (remaining > 0) visible.push({ label: `+${remaining}`, more: remaining })
      return visible
    }
  },

  // Field chips — story points, severity, environment, review status,
  // size, estimate. Each one only renders if the value is set, so cards
  // collapse cleanly when fields are empty.
  Fields: {
    display: (el, s) => (s.storyPoints || s.severity || s.environment || s.reviewStatus || s.size || s.estimate) ? 'flex' : 'none',
    flow: 'x',
    flexWrap: 'wrap',
    gap: 'Y',

    StoryPoints: {
      display: (el, s) => s.storyPoints != null ? 'inline-flex' : 'none',
      tag: 'span',
      align: 'center center',
      padding: 'V Z',
      round: 'C',
      fontSize: 'V',
      fontWeight: '700',
      color: 'cyan',
      background: 'color-mix(in srgb, var(--color-cyan) 14%, transparent)',
      text: (el, s) => `${s.storyPoints} SP`
    },
    Estimate: {
      display: (el, s) => (s.estimate != null && s.storyPoints == null) ? 'inline-flex' : 'none',
      tag: 'span',
      align: 'center center',
      padding: 'V Z',
      round: 'C',
      fontSize: 'V',
      fontWeight: '600',
      color: 'cyan',
      background: 'color-mix(in srgb, var(--color-cyan) 14%, transparent)',
      text: (el, s) => `~${s.estimate}h`
    },
    Size: {
      display: (el, s) => s.size ? 'inline-flex' : 'none',
      tag: 'span',
      align: 'center center',
      padding: 'V Z',
      round: 'C',
      fontSize: 'V',
      fontWeight: '600',
      color: 'subtitle',
      background: 'overlay',
      text: (el, s) => `Size ${s.size}`
    },
    Severity: {
      display: (el, s) => s.severity ? 'inline-flex' : 'none',
      tag: 'span',
      align: 'center center',
      padding: 'V Z',
      round: 'C',
      fontSize: 'V',
      fontWeight: '700',
      letterSpacing: '.04em',
      color: 'red',
      background: 'color-mix(in srgb, var(--color-red) 14%, transparent)',
      text: (el, s) => (s.severity || '').toUpperCase()
    },
    Environment: {
      display: (el, s) => s.environment ? 'inline-flex' : 'none',
      align: 'center center',
      gap: 'V',
      padding: 'V Z',
      round: 'C',
      fontSize: 'V',
      fontWeight: '500',
      color: 'subtitle',
      background: 'overlay',
      Icon: { icon: 'globe', boxSize: 'V' },
      Text: { tag: 'span', text: (el, s) => el.parent.parent.state.environment || '' }
    },
    ReviewStatus: {
      display: (el, s) => s.reviewStatus ? 'inline-flex' : 'none',
      tag: 'span',
      align: 'center center',
      padding: 'V Z',
      round: 'C',
      fontSize: 'V',
      fontWeight: '600',
      color: 'yellow',
      background: 'color-mix(in srgb, var(--color-yellow) 14%, transparent)',
      text: (el, s) => `Review · ${s.reviewStatus}`
    }
  },

  // Iteration + milestone + sub-issue progress + due date + parent +
  // tracked-by — the secondary metadata row. Hidden entirely when none
  // of these are populated.
  MetaRow: {
    display: (el, s) => (s.iterationTitle || s.milestone || s.subIssuesTotal > 0 || s.dueDate || s.parentIssueUrl || s.trackedByCount > 0) ? 'flex' : 'none',
    flow: 'x',
    flexWrap: 'wrap',
    gap: 'Z',
    fontSize: 'Y2',
    color: 'caption',

    Iteration: {
      display: (el, s) => s.iterationTitle ? 'inline-flex' : 'none',
      align: 'center left',
      gap: 'V',
      Icon: { icon: 'clock', boxSize: 'Z' },
      Text: { tag: 'span', text: (el, s) => el.parent.parent.state.iterationTitle || '' }
    },
    Milestone: {
      display: (el, s) => s.milestone ? 'inline-flex' : 'none',
      align: 'center left',
      gap: 'V',
      Icon: { icon: 'target', boxSize: 'Z' },
      Text: { tag: 'span', text: (el, s) => el.parent.parent.state.milestone || '' }
    },
    DueDate: {
      display: (el, s) => s.dueDate ? 'inline-flex' : 'none',
      align: 'center left',
      gap: 'V',
      Icon: { icon: 'clockOutline', boxSize: 'Z' },
      Text: { tag: 'span', text: (el, s) => el.parent.parent.state.dueDate || '' }
    },
    Parent: {
      display: (el, s) => s.parentIssueUrl ? 'inline-flex' : 'none',
      align: 'center left',
      gap: 'V',
      title: (el, s) => `Parent: ${s.parentIssueUrl}`,
      Icon: { icon: 'arrowAngleUp', boxSize: 'Z' },
      Text: { tag: 'span', text: 'parent' }
    },
    SubIssues: {
      display: (el, s) => s.subIssuesTotal > 0 ? 'inline-flex' : 'none',
      align: 'center left',
      gap: 'V',
      Icon: { icon: 'checkCircle', boxSize: 'Z' },
      Text: {
        tag: 'span',
        text: (el, s) => `${el.parent.parent.parent.state.subIssuesCompleted || 0}/${el.parent.parent.parent.state.subIssuesTotal}`
      }
    },
    TrackedBy: {
      display: (el, s) => s.trackedByCount > 0 ? 'inline-flex' : 'none',
      align: 'center left',
      gap: 'V',
      Icon: { icon: 'bookmark', boxSize: 'Z' },
      Text: { tag: 'span', text: (el, s) => `${el.parent.parent.state.trackedByCount}` }
    }
  },

  // Footer — divider then repo line + assignee + GH link. Sticks to the
  // bottom regardless of card content above.
  Footer: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    marginTop: 'auto',
    paddingTop: 'Z',

    RepoLine: {
      flow: 'x',
      align: 'center left',
      gap: 'Y',
      flex: '1',
      minWidth: '0',
      fontSize: 'V',
      color: 'caption',
      opacity: '.7',

      Repo: {
        display: (el, s) => s.repository ? 'inline' : 'none',
        tag: 'span',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        text: (el, s) => s.repository || ''
      },
      Number: {
        display: (el, s) => s.number ? 'inline' : 'none',
        tag: 'span',
        color: 'subtitle',
        text: (el, s) => s.number ? `#${s.number}` : ''
      }
    },

    // Avatar stack — up to MAX_VISIBLE_ASSIGNEES avatars overlapped left
    // → right, plus a "+N" pill if more remain. Falls back to GitHub
    // login when the email isn't on file (most external contributors
    // hide their email).
    Assignees: {
      display: (el, s) => {
        const root = el.state?.root
        if (!root) return 'none'
        return resolveAssignees(s, root.people || []).length > 0 ? 'flex' : 'none'
      },
      flow: 'x',
      align: 'center left',

      childExtends: 'TicketsAssigneeAvatar',
      childrenAs: 'state',
      children: (el, s) => {
        const ticket = el.parent?.parent?.state
        const root = el.state?.root
        if (!root || !ticket) return []
        const all = resolveAssignees(ticket, root.people || [])
        const visible = all.slice(0, MAX_VISIBLE_ASSIGNEES).map((a, i) => ({ ...a, _i: i, _more: 0 }))
        const remaining = all.length - MAX_VISIBLE_ASSIGNEES
        if (remaining > 0) {
          visible.push({ key: '_more', name: `+${remaining} more`, _i: visible.length, _more: remaining })
        }
        return visible
      }
    },

    Github: {
      display: (el, s) => s.githubUrl ? 'inline-flex' : 'none',
      extends: 'Link',
      href: (el, s) => s.githubUrl,
      target: '_blank',
      rel: 'noopener',
      onClick: (e) => e.stopPropagation(),
      align: 'center center',
      color: 'caption',
      opacity: '.6',
      ':hover': { color: 'title', opacity: '1' },
      Icon: { icon: 'github', boxSize: 'Z2' }
    }
  }
}

// One avatar in the assignee stack. The `_i` index drives the negative
// margin so the second+ avatars overlap the previous one (GitHub-style
// stacked look). The "+N more" variant ditches the image and renders a
// numeric badge instead.
export const TicketsAssigneeAvatar = {
  position: 'relative',
  width: 'B',
  height: 'B',
  marginLeft: (el, s) => s._i > 0 ? '-Y' : '0',
  zIndex: (el, s) => 10 - (s._i || 0),

  Img: {
    display: (el, s) => s._more ? 'none' : 'flex',
    tag: 'img',
    title: (el, s) => s.name || '',
    width: '100%',
    height: '100%',
    round: '50%',
    background: 'line',
    borderWidth: 'V',
    borderColor: 'document',
    borderStyle: 'solid',
    boxShadow: '0 1px 3px var(--color-shadow)',
    src: (el, s) => s.avatar,
    alt: (el, s) => s.name || ''
  },

  Badge: {
    display: (el, s) => s._more ? 'flex' : 'none',
    align: 'center center',
    width: '100%',
    height: '100%',
    round: '50%',
    fontSize: 'V',
    fontWeight: '700',
    color: 'subtitle',
    background: 'line',
    borderWidth: 'V',
    borderColor: 'document',
    borderStyle: 'solid',
    title: (el, s) => s.name || '',
    text: (el, s) => `+${s._more || 0}`
  }
}

// Single label chip. The `+N` overflow variant uses bolder text so it's
// distinguishable from the actual labels.
export const TicketsLabelChip = {
  tag: 'span',
  padding: 'V Z',
  round: 'C',
  fontSize: 'V',
  fontWeight: (el, s) => s.more ? '700' : '500',
  letterSpacing: '.02em',
  color: (el, s) => s.more ? 'caption' : 'subtitle',
  background: 'overlay',
  borderWidth: '1px',
  borderColor: 'line',
  borderStyle: 'solid',
  text: (el, s) => s.label
}
